import { useDispatch, useSelector } from "react-redux";
import UserAvatar from "../../../../../components/userAvatar/UserAvatar";
import BanIcon from "../../../../../components/icons/BanIcon";
import DeleteActionIcon from "../../../../../components/icons/DeleteActionIcon";
import PrimeBadge from "../../../../../components/primeBadge/PrimeBadge";
import SendNotification from "../../../../../components/sendNotification/SendNotification";
import { showConfirmationModal } from "../../../../../features/confirmationModal/ConfirmationModalSlice";
import { getLocalizedTime } from "../../../../../utils/timeZone";
import styles from "./oneUser.module.css";

const PRIME_TIER_LABELS = {
  prime1: "Plus",
  prime2: "Prime",
};

const OneUser = ({
  name,
  email,
  gameIds,
  region,
  userImage,
  addedTime,
  modifiedTime,
  userId,
  banned,
  isPrime,
  emailVerified,
  primeTier,
  primeExpiresAt,
  // The player's current squad (strings; empty when they have none).
  squadName = "",
  squadSlug = "",
}) => {
  const { timeZone } = useSelector((state) => state.timeZone);
  const { rolePermissions } = useSelector((state) => state.user);
  const can = (permission) => rolePermissions.includes(permission);
  const localizedAddedTime = getLocalizedTime(addedTime, timeZone);
  const localizedModifiedTime = getLocalizedTime(modifiedTime, timeZone);
  const localizedPrimeExpiresAt = primeExpiresAt
    ? getLocalizedTime(primeExpiresAt, timeZone)
    : null;
  const regionName =
    typeof region === "string" ? region : region?.name || region?.region || "-";
  const gamesCount = Array.isArray(gameIds) ? gameIds.length : 0;
  const userImageSrc =
    typeof userImage === "string" ? userImage : userImage?.path;

  const formatDateTime = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString();
  };

  const dispatch = useDispatch();

  const handleDeleteUser = () => {
    dispatch(
      showConfirmationModal({
        message: "هل انت متاكد من حذف اللاعب ؟",
        data: userId,
        actionType: "deleteUser",
      }),
    );
  };

  const handleBanUser = () => {
    dispatch(
      showConfirmationModal({
        message: banned
          ? "هل انت متأكد من ازالة الحظر ؟"
          : "هل انت متأكد من حظر اللاعب ؟",
        data: {
          userId,
          banned: !banned,
        },
        actionType: "banUser",
        confirmKind: banned ? "unban" : "ban",
      }),
    );
  };

  const handleDeleteGameId = (gameId) => {
    if (!gameId?._id) return;

    dispatch(
      showConfirmationModal({
        message: `هل انت متأكد من حذف حساب اللعبة (${
          gameId?.gameName || "-"
        } - ${gameId?.inGameId || "-"}) ؟`,
        data: {
          userId,
          gameIdDocId: gameId._id,
        },
        actionType: "deleteUserGameId",
      }),
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.contentWrapper}>
        <div className={styles.identityRow}>
          <div className={styles.avatar}>
            <UserAvatar src={userImageSrc} alt={name || "user"} />
          </div>
          <div className={styles.identityText}>
            <h3 className={styles.userName}>
              {name || "-"}
              <PrimeBadge isPrime={isPrime} primeTier={primeTier} size="medium" />
            </h3>
            <p className={styles.userEmail}>{email || "-"}</p>
          </div>
        </div>
        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>المنطقة</span>
            <span className={styles.summaryValue}>{regionName || "-"}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>السكواد</span>
            <span className={styles.summaryValue}>
              {squadName
                ? `${squadName}${squadSlug ? ` [${squadSlug}]` : ""}`
                : "-"}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>الحالة</span>
            <span
              className={
                banned
                  ? `${styles.summaryValue} ${styles.statusDanger}`
                  : `${styles.summaryValue} ${styles.statusSuccess}`
              }
            >
              {banned ? "محظور" : "نشط"}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>حسابات الألعاب</span>
            <span className={styles.summaryValue}>{gamesCount}</span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>البريد الإلكتروني</span>
            <span
              className={
                emailVerified
                  ? `${styles.summaryValue} ${styles.statusSuccess}`
                  : `${styles.summaryValue} ${styles.statusDanger}`
              }
            >
              {emailVerified ? "موثّق" : "غير موثّق"}
            </span>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>العضوية المميزة</span>
            <span
              className={
                isPrime
                  ? `${styles.summaryValue} ${styles.statusSuccess}`
                  : styles.summaryValue
              }
            >
              {isPrime ? "نعم" : "لا"}
            </span>
          </div>
          {isPrime && (
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>فئة العضوية</span>
              <span className={styles.summaryValue}>
                {PRIME_TIER_LABELS[primeTier] || primeTier || "-"}
              </span>
            </div>
          )}
          {isPrime && (
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>تنتهي العضوية</span>
              <span className={styles.summaryValue}>
                {formatDateTime(localizedPrimeExpiresAt)}
              </span>
            </div>
          )}
        </div>
        {gamesCount > 0 && (
          <div className={styles.gamesSection}>
            <span className={styles.sectionLabel}>تفاصيل الألعاب</span>
            <div className={styles.gamesGrid}>
              {gameIds.map((gameId, index) => {
                return (
                  <div
                    className={styles.gameCard}
                    key={`${gameId?.gameName || "game"}-${gameId?.inGameId || index}`}
                  >
                    <div className={styles.gameCardHeader}>
                      <span className={styles.gameMeta}>
                        اللعبة: {gameId?.gameName || "-"}
                      </span>
                      {can("delete-game-ids") && (
                        <button
                          type="button"
                          className={styles.gameDeleteButton}
                          onClick={() => handleDeleteGameId(gameId)}
                          title="حذف حساب اللعبة"
                          aria-label="حذف حساب اللعبة"
                        >
                          <DeleteActionIcon className={styles.gameDeleteIcon} />
                        </button>
                      )}
                    </div>
                    <span className={styles.gameMeta}>
                      الاسم: {gameId?.inGameName || "-"}
                    </span>
                    <span className={styles.gameMeta}>
                      الايدي: {gameId?.inGameId || "-"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className={styles.metaGrid}>
          <div className={styles.metaCard}>
            <span className={styles.metaLabel}>تمت الإضافة</span>
            <span className={styles.metaValue}>
              {formatDateTime(localizedAddedTime)}
            </span>
          </div>
          <div className={styles.metaCard}>
            <span className={styles.metaLabel}>آخر تعديل</span>
            <span className={styles.metaValue}>
              {formatDateTime(localizedModifiedTime)}
            </span>
          </div>
        </div>
      </div>
      <div className={styles.optionsWrapper}>
        {can("send-user-notification") && (
          <div className={styles.notificationAction}>
            <SendNotification
              targetType={"user"}
              type={"general"}
              targetId={userId}
            />
          </div>
        )}
        {can("ban-users") && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.moderationAction}`}
            onClick={handleBanUser}
          >
            <BanIcon className={styles.svgIcon} />
            <span>{banned ? "ازالة الحظر" : "حظر اللاعب"}</span>
          </button>
        )}
        {can("delete-users") && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.deleteAction}`}
            onClick={handleDeleteUser}
          >
            <DeleteActionIcon className={styles.svgIcon} />
            <span>حذف اللاعب</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default OneUser;
